import { ref, computed } from 'vue'

export interface EventLog {
  id: number
  timestamp: string
  model_name: string
  action: string
  instance_id: number
  details: string
  user: number
  user_username?: string
  ip_address?: string
  request_data?: any
  [key: string]: any // Add index signature to allow string indexing
}

export interface PaginatedEventLogResponse {
  count: number
  next: string | null
  previous: string | null
  results: EventLog[]
}

export function useEventLog() {
  const eventLogs = ref<EventLog[]>([])
  const totalCount = ref<number>(0)
  const currentPage = ref<number>(1)
  const pageSize = ref<number>(100)
  const loading = ref<boolean>(false)
  const hasNext = ref<boolean>(false)
  const hasPrevious = ref<boolean>(false)

  const getEventLogs = async (limit: number = 100, offset: number = 0): Promise<PaginatedEventLogResponse> => {
    const response = await fetch(`/api/event-logs/?limit=${limit}&offset=${offset}`, {
      credentials: 'include', // Required for CSRF token
    })
    
    if (!response.ok) {
      throw new Error('Failed to fetch Event Logs')
    }
    
    if (!response.headers.get('content-type')?.includes('application/json')) {
      throw new Error('Invalid content type')
    }

    return await response.json()
  }

  const fetchEventLog = async (page: number = 1, size: number = 100) => {
    loading.value = true
    try {
      const offset = (page - 1) * size
      const response = await getEventLogs(size, offset)
      
      eventLogs.value = response.results
      totalCount.value = response.count
      currentPage.value = page
      pageSize.value = size
      hasNext.value = response.next !== null
      hasPrevious.value = response.previous !== null
    } catch (error) {
      console.error('Error fetching event logs:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  const nextPage = async () => {
    if (hasNext.value) {
      await fetchEventLog(currentPage.value + 1, pageSize.value)
    }
  }

  const previousPage = async () => {
    if (hasPrevious.value) {
      await fetchEventLog(currentPage.value - 1, pageSize.value)
    }
  }

  const goToPage = async (page: number) => {
    if (page >= 1 && page <= totalPages.value) {
      await fetchEventLog(page, pageSize.value)
    }
  }

  const changePageSize = async (newSize: number) => {
    await fetchEventLog(1, newSize) // Reset to first page when changing page size
  }

  const totalPages = computed(() => {
    return Math.ceil(totalCount.value / pageSize.value)
  })

  const eventLogKeys = computed(() => {
    const keys = eventLogs.value.length > 0 ? Object.keys(eventLogs.value[0]) : []
    return keys
  })

  return { 
    eventLogs, 
    fetchEventLog, 
    eventLogKeys,
    totalCount,
    currentPage,
    pageSize,
    loading,
    hasNext,
    hasPrevious,
    totalPages,
    nextPage,
    previousPage,
    goToPage,
    changePageSize
  }
} 